import { lazy, useEffect } from 'react';
import {
  Navigate,
  NavigateOptions,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { selectActingUser } from 'redux/slices/actingUserSlice.slice';
import {
  selectSelectedTeamMemberId,
  selectTeamMember,
} from 'redux/slices/selectedMemberSlice.slice';
import { useAppDispatch, useAppSelector } from 'redux/storeHooks';
import { Routes, SyncHook } from '../routeTypes';
import { useNavigateToDocket } from '../navigationHooks.util';

// Lazy load the DocketPage component
const DocketPage = lazy(() =>
  import('../../../docket/DocketPage').then(module => ({
    default: module.default,
  }))
);

export const DOCKET_PREFIX_PATH = '/docket';

const routes: Routes = [
  {
    path: '/',
    element: <Navigate to="/docket" />,
  },
  {
    path: '/docket',
    element: <DocketPage />,
  },
  {
    path: '/docket/guide/:guideId',
    element: <DocketPage />,
  },
];

const useSyncHook: SyncHook = () => {
  const { guideId: routeGuideId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const reduxGuideId = useAppSelector(selectSelectedTeamMemberId);
  const actingUser = useAppSelector(selectActingUser);

  useEffect(() => {
    // Make sure there is a guideId in the route
    // If no guide in the route and there is in the redux, update the route to include a guideId
    if (routeGuideId) {
      navigate(`${DOCKET_PREFIX_PATH}/guide/${routeGuideId}`, {
        replace: true,
      });
    } else {
      if (actingUser) {
        navigate(`${DOCKET_PREFIX_PATH}/guide/${actingUser.id}`, {
          replace: true,
        });
      } else {
        navigate(`${DOCKET_PREFIX_PATH}`, {
          replace: true,
        });
      }
    }
    // Make sure the selected guide in redux matches the route guideId
    if (routeGuideId && routeGuideId !== reduxGuideId) {
      dispatch(selectTeamMember(routeGuideId));
    }
  }, [routeGuideId, reduxGuideId, actingUser, navigate, dispatch]);
};

const useNavigationHooks = () => {
  const dispatch = useAppDispatch();
  const { guideId: routeGuideId } = useParams();
  const navigateToDocket = useNavigateToDocket();

  return {
    toDocket: (guideId?: string, options?: NavigateOptions) => {
      // Determine which guideId will be used for navigation (same logic as in useNavigateToDocket)
      const effectiveGuideId =
        guideId && guideId !== 'none'
          ? guideId
          : routeGuideId && routeGuideId !== 'none'
          ? routeGuideId
          : undefined;

      // Dispatch Redux action to keep state in sync before navigation
      if (effectiveGuideId) {
        dispatch(selectTeamMember(effectiveGuideId));
      }

      // Use the hook for navigation
      navigateToDocket(guideId, options);
    },
  };
};

export const docketRouteConfig = {
  routes,
  useSyncHook,
  useNavigationHooks,
};
